import {
  type ComponentShape,
  flattenPolygonRing,
  type PlacementPosition,
  type ShapePoint,
  type ShapeVertex,
  shapeBoundsOf,
} from '@repo/api-contracts';
import { roundMm, shiftVertices } from '../drawn-shape';
import { translateVertices } from './move-vertex';

const roundPoint = (point: ShapePoint): ShapePoint => ({
  xMm: roundMm(point.xMm),
  yMm: roundMm(point.yMm),
});

const roundVertex = (vertex: ShapeVertex): ShapeVertex => ({
  ...roundPoint(vertex),
  ...(vertex.handleIn === undefined ? {} : { handleIn: roundPoint(vertex.handleIn) }),
  ...(vertex.handleOut === undefined ? {} : { handleOut: roundPoint(vertex.handleOut) }),
});

/** 점 편집 결과를 컴포넌트로 되돌린 값 — 모양(다각형)·외접 크기·새 배치 위치. */
export interface EditedShape {
  readonly shape: Extract<ComponentShape, { type: 'polygon' }>;
  readonly widthMm: number;
  readonly heightMm: number;
  readonly position: PlacementPosition;
}

/**
 * 표면 mm 로 고친 바깥 고리 점을 컴포넌트 mm 로 되돌린다. 풀어 쓴 고리(곡선 포함)의 외접 사각형 왼쪽 위를 새 원점·배치 위치로
 * 삼고, 점·손잡이를 그만큼 빼 1e-7 로 반올림한다. 가로·세로는 반올림한 점으로 다시 푼 고리의 외접 크기다.
 * 구멍(컴포넌트 mm)은 옛 위치와 새 위치의 차만큼 옮겨 사진 위 자리를 지킨다. 점이 없으면 null.
 */
export const normalizeEditedShape = (
  points: readonly ShapeVertex[],
  holes: readonly (readonly ShapeVertex[])[] | undefined,
  previousPosition: PlacementPosition,
): EditedShape | null => {
  if (points.length === 0) return null;
  const { left, top } = shapeBoundsOf(flattenPolygonRing(points));
  const local = translateVertices(points, -left, -top).map(roundVertex);
  const bounds = shapeBoundsOf(flattenPolygonRing(local));
  const position = { xMm: roundMm(left), yMm: roundMm(top) };
  const dx = previousPosition.xMm - position.xMm;
  const dy = previousPosition.yMm - position.yMm;
  const movedHoles = holes?.map((hole) => shiftVertices(hole, dx, dy));
  return {
    shape: {
      type: 'polygon',
      points: local,
      ...(movedHoles === undefined || movedHoles.length === 0 ? {} : { holes: movedHoles }),
    },
    widthMm: roundMm(bounds.widthMm),
    heightMm: roundMm(bounds.heightMm),
    position,
  };
};
